import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";
import { brandArticles, type BrandArticleData } from "@/mocks/brandArticles";
import JsonLd from "@/components/base/JsonLd";
import ArticleNavbar from "./ArticleNavbar";
import ArticleFooter from "./ArticleFooter";

interface BrandArticleProps {
  data: BrandArticleData;
}

const siteUrl = import.meta.env.VITE_SITE_URL || "https://kakobuyfinds.wiki";

function buildArticleSchema(data: BrandArticleData) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.pageTitle,
    description: data.metaDescription,
    author: {
      "@type": "Organization",
      name: "Kakobuy Finds Wiki",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Kakobuy Finds Wiki",
      url: siteUrl,
    },
    datePublished: "2026-07-01",
    dateModified: "2026-07-07",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/${data.slug}`,
    },
    inLanguage: "pl-PL",
  };
}

function buildBreadcrumbSchema(data: BrandArticleData) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Strona Główna",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: `${data.brandName} Spreadsheet`,
        item: `${siteUrl}/${data.slug}`,
      },
    ],
  };
}

export default function BrandArticle({ data }: BrandArticleProps) {
  const relatedArticles = brandArticles.filter((b) => b.slug !== data.slug).slice(0, 4);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleSchema(data)} />
      <JsonLd data={buildBreadcrumbSchema(data)} />
      <ArticleNavbar />

      {/* Article Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[540px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={data.images.heroBg}
            alt={`${data.brandName} Spreadsheet - kompletny przewodnik`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/50"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-1 text-center lg:text-left">
              <span className="inline-block px-4 py-1.5 rounded-full border border-white/30 text-white/90 text-xs font-medium mb-5 whitespace-nowrap">
                Przewodnik
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-white leading-[1.15] mb-4">
                {data.heroTagline}
              </h1>
              <p className="text-white/75 text-sm md:text-base max-w-lg mx-auto lg:mx-0 leading-relaxed">
                {data.heroSubtitle}
              </p>
              <div className="flex items-center gap-4 mt-6 justify-center lg:justify-start text-white/60 text-xs">
                <span>7 min czytania</span>
                <span className="w-1 h-1 rounded-full bg-white/40"></span>
                <span>Aktualizacja: lipiec 2026</span>
              </div>
            </div>
            <div className="hidden lg:flex flex-1 gap-4 justify-center">
              <div className="w-[180px] h-[240px] rounded-2xl overflow-hidden">
                <img
                  src={data.images.heroImg1}
                  alt={`${data.brandName} Spreadsheet produkty`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-[200px] h-[160px] rounded-2xl overflow-hidden mt-10">
                <img
                  src={data.images.heroImg2}
                  alt={`${data.brandName} Spreadsheet akcesoria`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="flex-1 prose max-w-none text-foreground-800 leading-relaxed space-y-8">
              {/* Intro Section */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  {data.intro.heading}
                </h2>
                {data.intro.paragraphs.map((p, i) => (
                  <p key={i} className="text-foreground-700 leading-relaxed">
                    {p.includes("stronę główną Kakobuy Finds Wiki") ? (
                      <>
                        {p.split("stronę główną Kakobuy Finds Wiki")[0]}
                        <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer">stronę główną Kakobuy Finds Wiki</Link>
                        {p.split("stronę główną Kakobuy Finds Wiki")[1] || ""}
                      </>
                    ) : p.includes("strony głównej Kakobuy Finds Wiki") ? (
                      <>
                        {p.split("strony głównej Kakobuy Finds Wiki")[0]}
                        <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer">strony głównej Kakobuy Finds Wiki</Link>
                        {p.split("strony głównej Kakobuy Finds Wiki")[1] || ""}
                      </>
                    ) : (
                      p
                    )}
                  </p>
                ))}
              </section>

              {/* Body Sections */}
              {data.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="text-foreground-700 leading-relaxed">
                      {p}
                    </p>
                  ))}
                  {section.listItems && section.listItems.length > 0 && (
                    <ul className="list-disc pl-5 space-y-2 text-foreground-700 my-3">
                      {section.listItems.map((item, lIdx) => (
                        <li key={lIdx}>{item}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              {/* Brand Showcase */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Najlepsze Marki Dostępne w {data.brandName} Spreadsheet
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  W <strong>{data.brandName} Spreadsheet</strong> znajdziesz produkty od najlepszych światowych marek.
                  Każda z nich jest regularnie aktualizowana, a nowe produkty są dodawane natychmiast po pojawieniu się na rynku.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
                  {data.brands.map((brand) => (
                    <span key={brand} className="px-3 py-2 bg-background-100 rounded-lg text-sm font-medium text-foreground-700 text-center">
                      {brand}
                    </span>
                  ))}
                </div>
              </section>

              {/* Summary */}
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 !mb-4">
                  Podsumowanie — Czy Warto Korzystać z {data.brandName} Spreadsheet?
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  Zdecydowanie tak. <strong>{data.brandName} Spreadsheet</strong> to jedno z najlepszych narzędzi
                  dostępnych na rynku dla polskich kupujących, którzy cenią swój czas i pieniądze. Dzięki rozbudowanemu
                  <strong> arkuszowi</strong>, codziennym aktualizacjom i szerokiemu wyborowi kategorii, <strong>{data.brandName} Spreadsheet</strong> jest
                  niezastąpionym towarzyszem każdego świadomego kupującego.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  Niezależnie od tego, czy szukasz limitowanych sneakersów, modnych bluz, czy unikalnych akcesoriów
                  — <strong>{data.brandName} Spreadsheet</strong> ma wszystko, czego potrzebujesz. Dołącz do rosnącej
                  społeczności zadowolonych użytkowników <strong>{data.brandName}</strong> i odkryj, jak łatwe mogą być zakupy online.
                </p>
              </section>

              {/* CTA + Back to home */}
              <div className="flex flex-col sm:flex-row items-center gap-4 pt-6 border-t border-background-200">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Powrót do strony głównej
                  <i className="ri-arrow-left-line"></i>
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-300 text-foreground-700 rounded-full text-sm font-semibold hover:bg-foreground-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  {data.brandName} Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block w-[280px] flex-shrink-0">
              <div className="sticky top-24">
                <div className="rounded-2xl overflow-hidden bg-background-100 mb-6">
                  <img
                    src={data.images.sidebarImg}
                    alt={`${data.brandName} Spreadsheet`}
                    className="w-full h-auto object-cover object-top"
                  />
                </div>

                <div className="p-5 rounded-2xl bg-background-100 mb-4">
                  <h4 className="font-heading font-bold text-foreground-950 text-sm mb-3">
                    Spis treści
                  </h4>
                  <ul className="space-y-2 text-xs text-foreground-600">
                    {data.intro.heading.length > 40 ? <li>{data.intro.heading.slice(0, 40)}...</li> : <li>{data.intro.heading}</li>}
                    {data.sections.map((s, i) => (
                      <li key={i}>{s.heading}</li>
                    ))}
                    <li>Najlepsze marki</li>
                    <li>Podsumowanie</li>
                  </ul>
                </div>

                <div className="p-5 rounded-2xl bg-primary-50">
                  <p className="text-xs font-semibold text-primary-700 mb-2">
                    Gotowy na zakupy?
                  </p>
                  <p className="text-xs text-foreground-600 mb-3">
                    Przejdź do {data.brandName} Spreadsheet i znajdź najlepsze produkty już teraz.
                  </p>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    {data.brandName} Sheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
            Inne Arkusze, Które Mogą Cię Zainteresować
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {relatedArticles.map((related) => (
              <Link
                key={related.slug}
                to={`/${related.slug}`}
                className="flex items-center gap-4 p-5 rounded-xl bg-background-50 hover:bg-primary-500 transition-all duration-300 group cursor-pointer"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 group-hover:bg-white/20 group-hover:text-white transition-colors flex-shrink-0">
                  <i className="ri-file-text-line"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-800 group-hover:text-white transition-colors">
                    {related.brandName} Spreadsheet
                  </p>
                  <p className="text-xs text-foreground-400 group-hover:text-white/70 transition-colors">
                    Przewodnik zakupowy
                  </p>
                </div>
                <i className="ri-arrow-right-line text-foreground-300 group-hover:text-white/70 ml-auto transition-colors"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Article CTA */}
      <section className="relative w-full min-h-[380px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={data.images.ctaBg}
            alt={`${data.brandName} Spreadsheet - zacznij zakupy`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
        </div>
        <div className="relative z-10 text-center px-4 md:px-6 max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-white mb-4">
            Gotowy na zakupy?
          </h2>
          <p className="text-white/80 text-sm md:text-base mb-8 leading-relaxed">
            Dołącz do tysięcy zadowolonych użytkowników i odkryj najlepsze okazje w {data.brandName} Spreadsheet.
          </p>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-3 px-10 py-4 bg-foreground-950 text-white rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
          >
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500">
              <i className="ri-shopping-bag-line text-white text-sm"></i>
            </span>
            {data.brandName} Sheet
            <i className="ri-arrow-right-up-line text-white/70"></i>
          </a>
        </div>
      </section>

      <ArticleFooter />
    </main>
  );
}